import { Calendar as MantineCalendar, CalendarProps } from '@mantine/dates';
import { forwardRef, useMemo } from 'react';
import { useMantineTheme } from '@mantine/core';
import { usePreferencesStore } from '../../../stores';
import { pickTextColorForBackground } from '../../../utils/color';

export type GradientVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'success'
  | 'info'
  | 'warning'
  | 'danger';

export interface ThemedCalendarProps extends CalendarProps {
  gradientVariant?: GradientVariant;
  selectedGradient?: boolean; // Apply gradient to selected date
  highlightToday?: boolean;
}

const Calendar = forwardRef<HTMLDivElement, ThemedCalendarProps>(
  (
    {
      gradientVariant = 'primary',
      selectedGradient = false,
      highlightToday = true,
      styles,
      ...rest
    },
    ref
  ) => {
    const theme = useMantineTheme();
    const reducedMotion = usePreferencesStore((state) => state.reducedMotion);

    if (!selectedGradient) {
      return (
        <MantineCalendar
          ref={ref}
          styles={styles}
          {...rest}
        />
      );
    }

    const gradient = theme.other?.gradients?.[gradientVariant] || {
      from: theme.colors.blue[6],
      to: theme.colors.cyan[6],
    };

    // Calculate text color for selected date on gradient background
    const selectedTextColor = useMemo(() => {
      const gradientString = `linear-gradient(135deg, ${gradient.from}, ${gradient.to})`;
      return pickTextColorForBackground(gradientString);
    }, [gradient]);

    return (
      <MantineCalendar
        ref={ref}
        styles={{
          ...styles,
          day: {
            ...(styles as any)?.day,
            '&[data-selected="true"]': {
              backgroundImage: `linear-gradient(135deg, ${gradient.from}, ${gradient.to})`,
              color: selectedTextColor,
              transition: reducedMotion ? 'none' : 'background-image 0.2s ease',
              ...(styles as any)?.day?.['&[data-selected="true"]'],
            },
            '&[data-selected="true"]:hover': {
              backgroundImage: `linear-gradient(135deg, ${gradient.from}, ${gradient.to})`,
              filter: 'brightness(1.1)',
              ...(styles as any)?.day?.['&[data-selected="true"]:hover'],
            },
          },
        }}
        {...rest}
      />
    );
  }
);

Calendar.displayName = 'Calendar';

export default Calendar;
